/* eslint-disable react-hooks/exhaustive-deps */
import { Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import ReactQuill from "react-quill";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import classNames from "classnames";
import PlansApi from "../../api/PlansApi";
import PageHeader from "../../components/PageHeader";
import ScrollToFirstError from "../../components/ScrollToFirstError";
import CardLoader from "../../components/content-loader/CardLoader";
import NumericInputField from "../../components/fields/NumericInputField";
import SelectInputField from "../../components/fields/SelectInputField";
import { ConfirmationModal } from "../../components/modals";
import { ButtonSpinner } from "../../components/placeholders";
import { QuillModules, publishStatusOptions } from "../../config/app-constants";
import { EActionItem, EToastvariant, IAFPlanForm, IPlanAddEdit } from "../../config/interfaces";
import { useAppDispatch } from "../../redux/Store";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";

const INIT_UF_VALUES: IAFPlanForm = {
  published: "",
  featured: false,
  title: "",
  subTitle: "",
  pricingValue: "",
  pricingSubTitle: "",
  description: "",
  incomeSources: [],
  footerContent: ""
};

const INITIAL_FORM_SCHEMA = {
  published: Yup.string().notRequired(),
  featured: Yup.boolean().required("Featured plan is required"),
  title: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Plane Title must be less than 255 characters")
    .required("Plan Title is required"),
  subTitle: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Plan Subtitle must be less than 255 characters")
    .notRequired(),
  pricingValue: Yup.string().trim().required("Pricing Value is required"),
  pricingSubTitle: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Pricing Subtitle must be less than 255 characters")
    .notRequired(),
  description: Yup.string().trim().notRequired(),
  incomeSources: Yup.array()
    .min(1, "Please select minimum one Income Sources")
    .required("Income Sources is required"),
  footerContent: Yup.string().trim().notRequired()
};

const AFPlanAddEdit = () => {
  const { uUId } = useParams();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isFetching, setIsFetching] = useState(true);
  const [initialFormValues, setInitialFormValues] = useState(INIT_UF_VALUES);
  const [isDeletePlan, setIsDeletePlan] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [incomeSourceList, setIncomeSourceList] = useState<any[]>([]);
  const [isPerformingAction, setIsPerformingAction] = useState<EActionItem>(EActionItem.NULL);

  useEffect(() => {
    getAFPlanDetails();
    getIncomeSourceList();
  }, []);

  const getIncomeSourceList = async () => {
    try {
      const plansApi = PlansApi.getInstance();
      const response = await plansApi.getIncomeSource();
      const _data = response.data;
      setIncomeSourceList(Object.values(_data));
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    }
  };

  const getAFPlanDetails = async () => {
    try {
      if (!uUId) {
        return;
      }

      setIsFetching(true);
      const plansApi = PlansApi.getInstance();
      const response = await plansApi.getPlanById(uUId);
      const _data = response;
      const _assistedFilingData = {
        published: _data?.publishedAt ? "true" : "false",
        featured: _data?.featured || false,
        title: _data?.title || "",
        subTitle: _data?.subTitle || "",
        pricingValue: _data?.pricingTitle || "",
        pricingSubTitle: _data?.pricingSubTitle || "",
        description: _data?.description || "",
        incomeSources: _data?.incomeSources || [],
        footerContent: _data?.footerContent || ""
      };
      setInitialFormValues(() => _assistedFilingData);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsFetching(false);
    }
  };

  const handleAddEditFormSubmit = async (
    values: IAFPlanForm,
    { setSubmitting, resetForm }: FormikHelpers<IAFPlanForm>
  ) => {
    try {
      setSubmitting(true);
      let _uUId = uUId;
      let _values: IPlanAddEdit = {
        title: values.title,
        subTitle: values.subTitle,
        pricingTitle: values.pricingValue,
        pricingSubTitle: values.pricingSubTitle,
        description: values.description,
        footerContent: values.footerContent,
        pricingRule: [
          {
            "minFilesCompleted": 1,
            "pricePerFile": values?.pricingValue
          }
        ],
        incomeSources: values.incomeSources,
        featured: values.featured,
        published: values.published
      };

      if (!_uUId) {
        _values.type = "assisted";
      }
      let _msg = `${_values?.title} added successfully`;
      const plansApi = PlansApi.getInstance();
      if (_uUId) {
        await plansApi.updatePlanById(_uUId, _values);
        _msg = `Changes applied to ${_values?.title} successfully`;
      } else {
        const data = await plansApi.addPlan(_values);
        _uUId = data?.uuid;
        resetForm();
      }
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Success",
          message: _msg,
          variant: EToastvariant.Success
        })
      );
      navigate("/plans/assisted-filings");
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePlan = async () => {
    try {
      if (!showDeleteModal) {
        return;
      }
      setIsDeletePlan(true);
      setIsPerformingAction(EActionItem.DELETE_AF_PLAN);
      let _msg = `${initialFormValues?.title} deleted successfully`;
      const _values = {
        uuids: [showDeleteModal]
      };
      const plansApi = PlansApi.getInstance();
      await plansApi.deletePlan(_values);
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Deleted",
          message: _msg,
          variant: EToastvariant.Success
        })
      );
      navigate("/plans/assisted-filings");
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsPerformingAction(EActionItem.NULL);
      setShowDeleteModal(null);
      setIsDeletePlan(false);
    }
  };

  return (
    <>
      <Container className="w-800">
        <PageHeader
          backIconButton={true}
          title={uUId ? initialFormValues?.title : "Add Assisted Filing Plan"}
        />

        {isFetching ? (
          <CardLoader cardType="details" />
        ) : (
          <Formik
            enableReinitialize={true}
            initialValues={initialFormValues}
            validationSchema={Yup.object().shape(INITIAL_FORM_SCHEMA)}
            onSubmit={handleAddEditFormSubmit}
          >
            {({
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              values,
              errors,
              touched,
              validateForm,
              isSubmitting,
              setTouched,
              submitCount,
              isValid
            }) => (
              <>
                <Form
                  onSubmit={async (event) => {
                    event.preventDefault();
                    await validateForm();
                    handleSubmit();
                  }}
                >
                  <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
                  <Card className="form-card">
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="inline-label">Status</Form.Label>
                        </Col>
                        <Col>
                          <Row className="align-items-center">
                            <Col sm={6}>
                              <SelectInputField
                                placeholder="Select status"
                                name="published"
                                menuZIndex={9}
                                value={
                                  values?.published
                                    ? {
                                        label: publishStatusOptions[values?.published],
                                        value: values.published
                                      }
                                    : null
                                }
                                options={UtilService.convertToArray(publishStatusOptions)}
                                onChange={(selectedStatus) => {
                                  if (selectedStatus) {
                                    setFieldValue("published", selectedStatus.value);
                                  } else {
                                    setFieldValue("published", undefined);
                                  }
                                }}
                                onBlur={handleBlur("published")}
                                isInvalid={Boolean(errors.published) && touched.published}
                                errorMsg={errors.published}
                                formType="GENERAL"
                              />
                            </Col>
                            <Col sm={6}>
                              <Form.Group className="form-group">
                                <Form.Check
                                  role="button"
                                  type="checkbox"
                                  id="featured"
                                  className="align-items-center"
                                >
                                  <Form.Check.Input
                                    type="checkbox"
                                    name="featured"
                                    checked={values.featured}
                                    onChange={(e) => {
                                      setFieldValue("featured", e.target.checked);
                                    }}
                                    isInvalid={Boolean(errors.featured) && touched.featured}
                                  />
                                  <Form.Check.Label>Mark Most Popular</Form.Check.Label>
                                  <Form.Control.Feedback type="invalid">
                                    {errors.featured}
                                  </Form.Control.Feedback>
                                </Form.Check>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="mb-0">Plan Header</Form.Label>
                        </Col>
                        <Col>
                          <Row className="align-items-center">
                            <Col sm={6}>
                              <Form.Group className="form-group" controlId="title">
                                <Form.Label className="small-label required-field">
                                  Plan Title
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder=""
                                  maxLength={255}
                                  name="title"
                                  value={values.title}
                                  onBlur={handleBlur("title")}
                                  onChange={handleChange("title")}
                                  isInvalid={Boolean(errors.title) && touched.title}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.title}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col sm={6}>
                              <Form.Group className="form-group" controlId="subTitle">
                                <Form.Label className="small-label">Plan Subtitle</Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder=""
                                  maxLength={255}
                                  name="subTitle"
                                  value={values.subTitle}
                                  onBlur={handleBlur("subTitle")}
                                  onChange={handleChange("subTitle")}
                                  isInvalid={Boolean(errors.subTitle) && touched.subTitle}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.subTitle}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="mb-0">Pricing Details</Form.Label>
                        </Col>
                        <Col>
                          <Row className="align-items-center">
                            <Col sm={6}>
                              <Form.Label className="required-field small-label">
                                Pricing Value
                              </Form.Label>
                              <NumericInputField
                                showLeftIcon={true}
                                placeholder="0.00"
                                name="pricingValue"
                                value={values.pricingValue}
                                onBlur={handleBlur("pricingValue")}
                                onChange={handleChange("pricingValue")}
                                isInvalid={Boolean(errors.pricingValue) && touched.pricingValue}
                                errorMsg={errors.pricingValue}
                                formType="GENERAL"
                              />
                            </Col>
                            <Col sm={6}>
                              <Form.Group className="form-group" controlId="pricingSubTitle">
                                <Form.Label className="small-label">Pricing Subtitle</Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder=""
                                  maxLength={255}
                                  name="pricingSubTitle"
                                  value={values.pricingSubTitle}
                                  onBlur={handleBlur("pricingSubTitle")}
                                  onChange={handleChange("pricingSubTitle")}
                                  isInvalid={
                                    Boolean(errors.pricingSubTitle) && touched.pricingSubTitle
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.pricingSubTitle}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Form.Group className="form-group" controlId="description">
                            <Form.Label>Plan Description</Form.Label>
                            <ReactQuill
                              theme="snow"
                              className="h-250"
                              modules={QuillModules}
                              value={values.description}
                              onChange={handleChange("description")}
                              placeholder="Enter plan description"
                            />
                            {Boolean(errors.description) && touched.description && (
                              <span className="field-error invalid">{errors.description}</span>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="required-field inline-label">
                            Income Sources included
                          </Form.Label>
                        </Col>
                        <Col>
                          <SelectInputField
                            name="incomeSources"
                            menuZIndex={9}
                            containerClasses={
                              Boolean(errors.incomeSources) &&
                              touched.incomeSources &&
                              "field-invalid"
                            }
                            isMulti={true}
                            isClearable={true}
                            placeholder="Select"
                            value={values?.incomeSources ? values.incomeSources : []}
                            options={incomeSourceList}
                            optionLabel="value"
                            optionValue="id"
                            onBlur={handleBlur("incomeSources")}
                            onChange={(selectedIncomeSources) => {
                              if (selectedIncomeSources?.length) {
                                setFieldValue("incomeSources", selectedIncomeSources);
                              } else {
                                setFieldValue("incomeSources", []);
                              }
                            }}
                            isInvalid={
                              Boolean(errors.incomeSources) && Boolean(touched.incomeSources)
                            }
                            errorMsg={String(errors.incomeSources)}
                            formType="GENERAL"
                          />
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="inline-label">Plan footer info</Form.Label>
                        </Col>
                        <Col>
                          <Form.Group className="form-group" controlId="footerContent">
                            <Form.Control
                              type="text"
                              autoComplete="false"
                              placeholder=""
                              maxLength={255}
                              name="footerContent"
                              value={values.footerContent}
                              onBlur={handleBlur("footerContent")}
                              onChange={handleChange("footerContent")}
                              isInvalid={Boolean(errors.footerContent) && touched.footerContent}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.footerContent}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      <div
                        className={classNames("form-btns-wrap", uUId && "justify-content-between")}
                      >
                        <Button
                          size="lg"
                          variant={uUId ? "outline-danger" : "outline-greyDark"}
                          className="btn-w-120"
                          onClick={() => {
                            uUId ? setShowDeleteModal(uUId) : navigate("/plans/assisted-filings");
                          }}
                        >
                          {isDeletePlan ? <ButtonSpinner /> : uUId ? "Delete" : "Cancel"}
                        </Button>
                        <Button
                          size="lg"
                          variant="primary"
                          className="btn-w-160"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <ButtonSpinner /> : uUId ? "Update" : "Add"}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
        )}
      </Container>

      {showDeleteModal && (
        <ConfirmationModal
          showModal={!!showDeleteModal}
          onYesBtnClick={() => handleDeletePlan()}
          onNoBtnClick={() => {
            setShowDeleteModal(null);
          }}
          title="Please confirm"
          message={`Are you sure you want to  delete "${initialFormValues?.title}"?`}
          onYesBtnText="Confirm"
          onNoBtnText="Cancel"
          isProcessing={isPerformingAction === EActionItem.DELETE_AF_PLAN}
        />
      )}
    </>
  );
};

export default AFPlanAddEdit;
